import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Compass } from "lucide-react";
import SEOMeta from "../components/SEOMeta";
import { Reveal, MaskedLine } from "../components/Reveal";
import { useContent, useBusinesses } from "../lib/content";

const VentureCard = ({ biz, large }) => (
  <div
    data-testid={`business-card-${biz.id}`}
    className={`group relative overflow-hidden border border-white/10 hover:border-gold/50 transition-colors duration-500 ${
      large ? "md:col-span-2 min-h-[340px] md:min-h-[460px]" : "min-h-[340px]"
    }`}
  >
    {biz.image_url ? (
      <img
        src={biz.image_url}
        alt={biz.name}
        className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-65 group-hover:scale-105 transition-[transform,opacity] duration-700"
      />
    ) : (
      <div className="absolute inset-0 bg-navy-700" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
    {biz.status === "upcoming" && (
      <span className="absolute top-6 right-6 font-mono text-[9px] tracking-[0.25em] uppercase text-gold border border-gold/40 px-3 py-1.5 rounded-full bg-navy/60 backdrop-blur">
        Coming Soon
      </span>
    )}
    <div className="relative h-full flex flex-col justify-end p-8 sm:p-10">
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold mb-3">{biz.category}</p>
      <h3 className="font-display text-2xl sm:text-3xl text-white mb-3">{biz.name}</h3>
      <p className="text-sm text-white/60 max-w-md leading-relaxed">{biz.description}</p>
      <span className="mt-5 inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-white/70 group-hover:text-gold transition-colors duration-300">
        Learn More <ArrowRight size={12} />
      </span>
    </div>
  </div>
);

export default function Businesses() {
  const content = useContent();
  const businesses = useBusinesses();

  return (
    <div>
      <SEOMeta
        title="Our Businesses | Touson Ventures (OPC) Private Limited"
        description="Explore the businesses and ventures of Touson Ventures — an Indian business company building opportunities across promising sectors in India."
      />

      <section className="pt-44 pb-16 sm:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.06),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-gold mb-8"
            data-testid="businesses-eyebrow"
          >
            OUR BUSINESSES
          </motion.p>
          <h1 className="font-display tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="businesses-headline">
            <MaskedLine delay={0.15}>Ventures built to</MaskedLine>
            <MaskedLine delay={0.32} className="gold-gradient-text italic">matter and to last.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-2xl text-white/65 text-base sm:text-lg leading-relaxed"
            data-testid="businesses-intro"
          >
            {content.businesses_page.intro}
          </motion.p>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="grid md:grid-cols-3 gap-6" data-testid="businesses-grid">
            {businesses.map((biz, i) => (
              <Reveal key={biz.id} delay={i * 0.08} className={i === 0 ? "md:col-span-2" : ""}>
                <Link to="/contact" className="block h-full">
                  <VentureCard biz={biz} large={i === 0} />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="border border-gold/25 bg-gradient-to-br from-navy-800 to-navy p-10 sm:p-16 relative overflow-hidden" data-testid="businesses-explore-panel">
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
              <div className="relative grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-8">
                  <Compass size={26} className="text-gold mb-6" strokeWidth={1.5} />
                  <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight mb-5">
                    Exploring what comes next.
                  </h2>
                  <p className="text-white/60 leading-relaxed max-w-2xl" data-testid="businesses-explore-text">
                    {content.businesses_page.explore_text}
                  </p>
                </div>
                <div className="lg:col-span-4 lg:text-right">
                  <Link
                    to="/contact"
                    data-testid="businesses-partner-button"
                    className="group inline-flex items-center gap-3 bg-gold text-navy font-medium text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300"
                  >
                    Partner With Us
                    <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
